import React, { useEffect, useState } from "react";
import { Card, Button, Tab, Nav } from "react-bootstrap";
import './OpalPlanningCenter.css';
import TauriImage from "../TauriImage/TauriImage";
import { planStatusDisplayNames, planWorksheetStatusMapping } from "../../visualizations/visualizations";
import OpalPlanSelectionDateModal from "./OpalPlanSelectionDateModal/OpalPlanSelectionDateModal";
import appURL from '../../appURL.json';
import { addRecord, getAll, getNextId, getRecordById } from "../../localDB";
import TauriLoader2 from "../TauriLoader2/TauriLoader2";
import { Link, useNavigate } from "react-router-dom";
import OpalAlertModal from "../OpalAlertModal/OpalAlertModal";
import { transformWorksheets } from "../OpalPlanningLayout/PlanningFactoryData/PlanningFactoryData";
import ApprovablePlans from "../OpalPlanningLayout/ApprovablePlans/ApprovablePlans";
import SubordinatePlans from "../OpalPlanningLayout/SubordinatePlans/SubordinatePlans";
import { transformSyncPolicies } from "../OpalSyncFactoryData/OpalSyncFactoryData";

const OpalPlanningCenter = () => {
    const [showSelectDate, setShowSelectDate] = useState();
    const [worksheetLoading, setWorksheetLoading] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const [plans, setPlans] = useState([]);
    const [showAlertModal, setShowAlertModal] = useState(false);
    const [alertModalProps, setAlertModalProps] = useState({
        title: "",
        bodyText: "",
        confirmButtonText: "",
        cancelButtonText: "",
        Icon: "",
        modalType: ""
    });

    const navigate = useNavigate();

    const storedPermission = localStorage.getItem("permission");
    const permission = JSON.parse(atob(storedPermission));
    const [activeTab, setActiveTab] = useState(permission?.planning?.approve_plan ? "approvablePlans" : "myPlans");

    const statusColors = {
        0: "#FD9B08", // For Submission
        1: "#138BE1", // Awaiting Approval
        2: "#000000", // Closed
        3: "#069D33", // Approved
        4: "#C22020", // Rejected
        5: "#FC9A08", // Update Required
    };

    const onCreate = async (cycle) => {
        if (!cycle) return;

        const isOnline = JSON.parse(localStorage.getItem("isOnline"));
        const hasInternet = navigator.onLine;
        setIsCreating(true);
        try {
            if (isOnline && hasInternet) {
                await onlineCreateWorksheet(cycle);
            } else {
                await offlineCreateWorksheet(cycle);
            }
        } catch (err) {
            console.log(err);
        } finally {
            setIsCreating(false);
        }
    };

    const offlineCreateWorksheet = async (cycle) => {
        try {
            const nextID = await getNextId("Worksheets");

            const newWorksheet = {
                ID: nextID,
                EmployeeID: Number(permission?.coverage?.employee_id),
                PlanNumber: "",
                Cycle: {
                    ID: cycle.ID,
                    Code: cycle.Code,
                    Description: cycle.Description,
                    StartDate: cycle?.StartDate,
                    EndDate: cycle?.EndDate
                },
                Status: planWorksheetStatusMapping.ForSubmission,
                Days: {}
            }

            await addRecord("Worksheets", newWorksheet);

            setShowSelectDate(false);
            navigate(`/planning/${nextID}`);
        } catch (err) {
            setAlertModalProps({
                title: "Failed",
                bodyText: "Faled to create!",
                confirmButtonText: "",
                cancelButtonText: "Ok",
                Icon: "",
                modalType: "message"
            });

            setShowAlertModal(true);
            return;
        }
    }

    const onlineCreateWorksheet = async (cycle) => {
        try {
            const c_id = localStorage.getItem("c_id");
            const token = localStorage.getItem("token");
            const devId = localStorage.getItem("devId");

            const response = await fetch(`${appURL.API_Worksheet}/create/${Number(permission?.coverage?.employee_id)}/${Number(cycle?.ID)}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                    'X-ETR-Device-ID': devId
                }
            });

            if (!response.ok) {

                let errorData = null;
                try {
                    errorData = await response.json();
                } catch {
                    errorData = {};
                }

                setAlertModalProps({
                    title: "Failed",
                    bodyText: errorData?.description || "Plan creation failed.",
                    confirmButtonText: "",
                    cancelButtonText: "Ok",
                    Icon: "",
                    modalType: "message"
                });

                setShowAlertModal(true);
                return;
            }

            const data = await response.json();

            const newWorksheet = {
                ID: data.id,
                EmployeeID: Number(permission?.coverage?.employee_id),
                PlanNumber: "",
                Cycle: {
                    ID: cycle?.ID,
                    Code: cycle?.Code,
                    Description: cycle?.Description,
                    StartDate: cycle?.StartDate,
                    EndDate: cycle?.EndDate
                },
                Status: planWorksheetStatusMapping.ForSubmission,
                Days: data?.days || {}
            }

            await addRecord("Worksheets", newWorksheet);

            setShowSelectDate(false);
            navigate(`/planning/${data.id}`);
        } catch (err) {
            setAlertModalProps({
                title: "Failed",
                bodyText: "Plan creation failed.",
                confirmButtonText: "",
                cancelButtonText: "Ok",
                Icon: "",
                modalType: "message"
            });

            setShowAlertModal(true);
        }
    }

    const fetchOnlineWorksheet = async () => {
        try {
            const token = localStorage.getItem("token");
            const devId = localStorage.getItem("devId");
            const response = await fetch(`${appURL.API_Worksheet}/${Number(permission?.coverage?.employee_id)}/worksheets`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                    'X-ETR-Device-ID': devId
                }
            });

            if (!response.ok) {
                setAlertModalProps({
                    title: "Failed",
                    bodyText: "Failed to load plan",
                    confirmButtonText: "",
                    cancelButtonText: "Ok",
                    Icon: "",
                    modalType: "message"
                });

                setShowAlertModal(true);
                return;
            }

            const data = await response.json();
            const transformedWorksheets = await transformWorksheets(data.worksheets);

            const sortedWorksheets = transformedWorksheets
                .sort((a, b) => {
                    const statusA = a.Status;
                    const statusB = b.Status;

                    if (statusA !== statusB) {
                        return statusA - statusB;
                    }

                    return b.ID - a.ID;
                })
                .slice(0, 8);

            setPlans(sortedWorksheets);
        } catch (err) {
            setAlertModalProps({
                title: "Failed",
                bodyText: "Failed to load plan",
                confirmButtonText: "",
                cancelButtonText: "Ok",
                Icon: "",
                modalType: "message"
            });

            setShowAlertModal(true);
        }
    }

    const fetchOfflineWorksheet = async () => {
        try {
            const response = await getAll("Worksheets");

            const sortedWorksheets = response
                .sort((a, b) => {
                    const statusA = a.Status;
                    const statusB = b.Status;

                    if (statusA !== statusB) {
                        return statusA - statusB;
                    }

                    return b.ID - a.ID;
                })
                .slice(0, 8);

            setPlans(sortedWorksheets);

        } catch (err) {
            console.log(err);
        }
    }

    const fetchPolicies = async () => {
        try {

            const token = localStorage.getItem("token");
            const devId = localStorage.getItem("devId");
            const usr_g = localStorage.getItem("usr_g");

            const policy = getRecordById("Policies", Number(usr_g));
            if (policy && policy?.Policies?.length > 0) return;

            const response = await fetch(`${appURL.API_Sync}/operations/policies/1/${Number(usr_g)}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                    'X-ETR-Device-ID': devId
                }
            });

            if (!response.ok) {
                return;
            }

            const data = await response.json();

            await addRecord("Policies", {
                ID: Number(usr_g),
                Policies: data?.policies
            })

        } catch (err) {
            return;
        }
    }

    const fetchData = async () => {
        try {
            setWorksheetLoading(true);
            const isOnline = JSON.parse(localStorage.getItem("isOnline"));
            const hasInternet = navigator.onLine;

            if (isOnline && hasInternet) {
                await fetchOnlineWorksheet();
                await fetchPolicies();
            } else {
                await fetchOfflineWorksheet();
            }
        } catch (error) {
            console.error(error);
        } finally {
            setWorksheetLoading(false);
        }
    };

    const handleCreate = () => {
        if (!permission?.planning?.create) {
            setAlertModalProps({
                title: "Message",
                bodyText: "You don't have the permission to do this action.",
                confirmButtonText: "",
                cancelButtonText: "Ok",
                Icon: "",
                modalType: "message"
            });

            setShowAlertModal(true);
            return;
        }
        setShowSelectDate(true);
    }

    useEffect(() => {
        fetchData();
    }, [])

    return (
        <div className="opal-planning-center">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-2 planning-center-header">
                <div className="panel-title">Coverage Plans</div>
                <div className="d-flex align-items-center">
                    <Nav variant="tabs"
                        className="planning-tabs"
                        activeKey={activeTab}           // <-- control active key manually
                        onSelect={(k) => setActiveTab(k)}>
                        {permission?.planning?.approve_plan &&
                            <>
                                <Nav.Item>
                                    <Nav.Link eventKey="subordinatePlans">Team Plans</Nav.Link>
                                </Nav.Item>
                                <Nav.Item>
                                    <Nav.Link eventKey="approvablePlans">Approvable Plans</Nav.Link>
                                </Nav.Item>
                            </>
                        }
                        <Nav.Item>
                            <Nav.Link eventKey="myPlans">My Plans</Nav.Link>
                        </Nav.Item>
                    </Nav>
                    <Button
                        onClick={handleCreate}
                        className="d-flex align-items-center"
                    >
                        <TauriImage localImage={"/Images/flat-color-icons--plus-v5.svg"} alt="add new" className={"add-new"} />
                        Create New
                    </Button>
                </div>
            </div>
            <div className="horizontal-line"></div>

            <Tab.Container activeKey={activeTab}>
                <Tab.Content>
                    <Tab.Pane eventKey="myPlans">
                        {worksheetLoading ? (
                            <TauriLoader2 width="2.5rem" height="2.5rem" borderwidth="0.35rem" color="#1D5D9B" />
                        ) : (
                            <>
                                {plans.length > 0 ? (
                                    <div className="plan-list" style={{ "--col-count": Math.min(plans.length + 1, 4) }}>
                                        {plans.map((plan, index) => (
                                            <Link to={`/planning/${plan.ID}`} key={index} className="text-center card plan-card">
                                                <Card.Body className="d-flex flex-column align-items-center justify-content-between">
                                                    <div>
                                                        <div className="mb-2 text-primary">
                                                            <TauriImage localImage={"/Images/fe--calendar.svg"} alt="calendar" />
                                                        </div>
                                                        <div className="plan-title">{plan?.Cycle?.Code}</div>
                                                        <div className="plan-description">{plan?.Cycle?.Description}</div>
                                                    </div>
                                                    <div className="plan-status" style={{ color: statusColors[plan?.Status] }}>
                                                        {planStatusDisplayNames[plan?.Status]}
                                                    </div>
                                                </Card.Body>
                                            </Link>
                                        ))}

                                        {plans.length < 8 && (
                                            <Card className="text-center plan-card" onClick={handleCreate}>
                                                <Card.Body className="d-flex flex-column align-items-center justify-content-center">
                                                    <div className="mb-2 text-success">
                                                        <TauriImage localImage={"/Images/flat-color-icons--plus-v4.svg"} alt="add new" className={"add-new"} />
                                                    </div>
                                                    <div className="plan-addnew-description">Add New</div>
                                                </Card.Body>
                                            </Card>
                                        )}
                                    </div>
                                ) : (
                                    <div className="no-plans">
                                        <Card.Body className="d-flex flex-column align-items-center justify-content-center" onClick={handleCreate}>
                                            <div className="description">A Coverage Plan has not yet been created</div>
                                            <div className="my-4 text-success">
                                                <TauriImage localImage={"/Images/flat-color-icons--plus-v4.svg"} alt="add new" className={"add-new"} />
                                            </div>
                                            <div className="createnew-text">Create New</div>
                                        </Card.Body>
                                    </div>
                                )}
                            </>
                        )}
                    </Tab.Pane>

                    {permission?.planning?.approve_plan && (
                        <Tab.Pane eventKey="subordinatePlans">
                            <SubordinatePlans />
                        </Tab.Pane>
                    )}

                    {permission?.planning?.approve_plan && (
                        <Tab.Pane eventKey="approvablePlans">
                            <ApprovablePlans />
                        </Tab.Pane>
                    )}

                </Tab.Content>
            </Tab.Container>

            <OpalPlanSelectionDateModal
                show={showSelectDate}
                onHide={() => setShowSelectDate(false)}
                loading={isCreating}
                onClick={onCreate}
            />

            <OpalAlertModal
                show={showAlertModal}
                onHide={() => setShowAlertModal(false)}
                title={alertModalProps.title}
                bodyText={alertModalProps.bodyText}
                confirmButtonText={alertModalProps.confirmButtonText}
                cancelButtonText={alertModalProps.cancelButtonText}
                modalType={alertModalProps.modalType}
                Icon={alertModalProps.Icon}
            />
        </div>
    );
};

export default OpalPlanningCenter;
